import React, { useState, useEffect, useRef } from 'react';
import type { LayoutInfo } from './ProductPreview';

interface PrintAreaEditorProps {
    layout: LayoutInfo;
    borderTop: number;
    borderBottom: number;
    borderLeft: number;
    borderRight: number;
    onUpdateBorders: (top: number, bottom: number, left: number, right: number) => void;
}

export const PrintAreaEditor: React.FC<PrintAreaEditorProps> = ({
    layout,
    borderTop,
    borderBottom,
    borderLeft,
    borderRight,
    onUpdateBorders
}) => {
    const [rect, setRect] = useState({ x: 0, y: 0, w: 0, h: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [isResizing, setIsResizing] = useState<string | null>(null);
    const [bounds, setBounds] = useState({ x: 0, y: 0, w: 0, h: 0 });

    const dragStartRef = useRef({ x: 0, y: 0 });
    const rectStartRef = useRef({ x: 0, y: 0, w: 0, h: 0 });

    // Sync state with props
    useEffect(() => {
        if (isDragging || isResizing) return;

        const {
            canvasLeft, canvasTop,
            frameWidthMm,
            matLeft, matTop, matRight, matBottom,
            pixelToMmScale, outerWidthMm, outerHeightMm
        } = layout;

        const boundX = canvasLeft + (frameWidthMm + matLeft) * pixelToMmScale;
        const boundY = canvasTop + (frameWidthMm + matTop) * pixelToMmScale;
        const boundW = (outerWidthMm - (frameWidthMm * 2) - matLeft - matRight) * pixelToMmScale;
        const boundH = (outerHeightMm - (frameWidthMm * 2) - matTop - matBottom) * pixelToMmScale;

        setBounds({ x: boundX, y: boundY, w: boundW, h: boundH });

        const rectX = boundX + borderLeft * pixelToMmScale;
        const rectY = boundY + borderTop * pixelToMmScale;
        const rectW = boundW - (borderLeft + borderRight) * pixelToMmScale;
        const rectH = boundH - (borderTop + borderBottom) * pixelToMmScale;

        setRect({ x: rectX, y: rectY, w: rectW, h: rectH });
    }, [layout, borderTop, borderBottom, borderLeft, borderRight, isDragging, isResizing]);

    const handleMouseDown = (e: React.MouseEvent, type: 'move' | 'tl' | 'tr' | 'bl' | 'br') => {
        e.preventDefault();
        e.stopPropagation();

        console.log('MouseDown:', type);

        if (type === 'move') {
            setIsDragging(true);
        } else {
            setIsResizing(type);
        }

        dragStartRef.current = { x: e.clientX, y: e.clientY };
        rectStartRef.current = { ...rect };
    };

    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            if (!isDragging && !isResizing) return;

            e.preventDefault(); // Prevent default text selection during drag

            const dx = e.clientX - dragStartRef.current.x;
            const dy = e.clientY - dragStartRef.current.y;
            const start = rectStartRef.current;

            let newRect = { ...start };

            if (isDragging) {
                let newX = start.x + dx;
                let newY = start.y + dy;

                newX = Math.max(bounds.x, Math.min(newX, bounds.x + bounds.w - start.w));
                newY = Math.max(bounds.y, Math.min(newY, bounds.y + bounds.h - start.h));

                newRect.x = newX;
                newRect.y = newY;
            }
            else if (isResizing) {
                if (isResizing === 'tl') {
                    const x = Math.min(Math.max(bounds.x, start.x + dx), start.x + start.w - 10);
                    const y = Math.min(Math.max(bounds.y, start.y + dy), start.y + start.h - 10);
                    newRect.x = x;
                    newRect.y = y;
                    newRect.w = start.w + (start.x - x);
                    newRect.h = start.h + (start.y - y);
                }
                else if (isResizing === 'tr') {
                    const y = Math.min(Math.max(bounds.y, start.y + dy), start.y + start.h - 10);
                    const w = Math.min(Math.max(10, start.w + dx), bounds.x + bounds.w - start.x);
                    newRect.y = y;
                    newRect.w = w;
                    newRect.h = start.h + (start.y - y);
                }
                else if (isResizing === 'bl') {
                    const x = Math.min(Math.max(bounds.x, start.x + dx), start.x + start.w - 10);
                    const h = Math.min(Math.max(10, start.h + dy), bounds.y + bounds.h - start.y);
                    newRect.x = x;
                    newRect.w = start.w + (start.x - x);
                    newRect.h = h;
                }
                else if (isResizing === 'br') {
                    const w = Math.min(Math.max(10, start.w + dx), bounds.x + bounds.w - start.x);
                    const h = Math.min(Math.max(10, start.h + dy), bounds.y + bounds.h - start.y);
                    newRect.w = w;
                    newRect.h = h;
                }
            }

            setRect(newRect);
        };

        const handleMouseUp = () => {
            if (isDragging || isResizing) {
                console.log('MouseUp - Committing details');
                const { pixelToMmScale } = layout;

                const bLeft = Math.round((rect.x - bounds.x) / pixelToMmScale);
                const bTop = Math.round((rect.y - bounds.y) / pixelToMmScale);
                const bRight = Math.round(((bounds.x + bounds.w) - (rect.x + rect.w)) / pixelToMmScale);
                const bBottom = Math.round(((bounds.y + bounds.h) - (rect.y + rect.h)) / pixelToMmScale);

                console.log('New Borders (Mm):', { bTop, bBottom, bLeft, bRight });

                onUpdateBorders(
                    Math.max(0, bTop),
                    Math.max(0, bBottom),
                    Math.max(0, bLeft),
                    Math.max(0, bRight)
                );
            }
            setIsDragging(false);
            setIsResizing(null);
        };

        if (isDragging || isResizing) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
        }

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging, isResizing, bounds, layout, onUpdateBorders, rect]); // Added rect as dependency for closure correctness in handleMouseUp? No, logic uses current 'rect' state. Yes, handleMouseUp needs current 'rect'.

    const handleStyle = (cursor: string): React.CSSProperties => ({
        position: 'absolute',
        width: '16px', // Increased touch target
        height: '16px',
        backgroundColor: '#fff',
        border: '1px solid #3b82f6',
        borderRadius: '50%',
        cursor: cursor,
        zIndex: 50, // High z-index
        pointerEvents: 'auto' // Explicitly enable pointer events
    });

    return (
        <div style={{
            position: 'absolute',
            top: 0, left: 0, right: 0, bottom: 0,
            pointerEvents: 'none',
            zIndex: 40 // Overlay above everything
        }}>
            <div
                style={{
                    position: 'absolute',
                    left: `${rect.x}px`,
                    top: `${rect.y}px`,
                    width: `${rect.w}px`,
                    height: `${rect.h}px`,
                    border: '2px solid #3b82f6',
                    backgroundColor: 'rgba(59, 130, 246, 0.2)',
                    pointerEvents: 'auto',
                    cursor: 'move',
                    boxSizing: 'border-box'
                }}
                onMouseDown={(e) => handleMouseDown(e, 'move')}
            >
                {/* Handles - Offset significantly to be center-aligned on corners */}
                <div style={{ ...handleStyle('nw-resize'), top: '-8px', left: '-8px' }} onMouseDown={(e) => handleMouseDown(e, 'tl')} />
                <div style={{ ...handleStyle('ne-resize'), top: '-8px', right: '-8px' }} onMouseDown={(e) => handleMouseDown(e, 'tr')} />
                <div style={{ ...handleStyle('sw-resize'), bottom: '-8px', left: '-8px' }} onMouseDown={(e) => handleMouseDown(e, 'bl')} />
                <div style={{ ...handleStyle('se-resize'), bottom: '-8px', right: '-8px' }} onMouseDown={(e) => handleMouseDown(e, 'br')} />
            </div>
        </div>
    );
};
